import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { STORAGE_UPDATE_TYPE, StoreState, UpdateStrategy } from '@/zustand/types';
import { EncryptedStorage } from './encrypted-storage';

interface CreateStoreOptions {
	shouldPersist?: boolean;
}

export const createBaseStore = <T>(storageKey: string, initialState: T, customActions?: any, options: CreateStoreOptions = { shouldPersist: true }) => {
	const baseActions: StateCreator<
		StoreState<T> & {
			isHydrated: boolean;
			setHydrated: (state: boolean) => void;
		}
	> = (set, get) => ({
		data: initialState,
		isLoading: false,
		error: null,
		isHydrated: false,
		getData() {
			return get().data;
		},
		setData: (newData: T) => set({ data: newData }),
		updateData: (partialData: Partial<T>, strategy: UpdateStrategy) =>
			set(state => ({
				data: strategy === STORAGE_UPDATE_TYPE.MERGE ? ({ ...state.data, ...partialData } as T) : ({ ...initialState, ...partialData } as T)
			})),
		clearData: () => set({ data: initialState }),
		setLoading: (isLoading: boolean) => set({ isLoading }),
		setError: (error: string | null) => set({ error }),
		setHydrated: (state: boolean) => set({ isHydrated: state }),
		...customActions?.(set, get)
	});

	if (!options.shouldPersist) {
		return create<
			StoreState<T> & {
				isHydrated: boolean;
				setHydrated: (state: boolean) => void;
			}
		>()(baseActions);
	}

	const persistOptions: PersistOptions<
		StoreState<T> & {
			isHydrated: boolean;
			setHydrated: (state: boolean) => void;
		}
	> = {
		name: storageKey,
		storage: new EncryptedStorage(),
		onRehydrateStorage: () => state => {
			state?.setHydrated(true);
		}
	};

	return create<
		StoreState<T> & {
			isHydrated: boolean;
			setHydrated: (state: boolean) => void;
		}
	>()(persist(baseActions, persistOptions));
};
